// Dynamic Icon Loader for DogGoods Store
// Handles efficient loading and rendering of SVG icons

class DogGoodsIconLoader {
  constructor() {
    this.icons = window.DogGoodsIcons || {};
    this.loadedIcons = new Set();
    this.observedElements = new WeakSet();
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }
  
  initialize() {
    this.initializeIcons();
    this.setupLazyLoading();
    this.setupMutationObserver();
  }
  
  // Load icon by name and insert into element
  loadIcon(iconName, element, className = '') {
    if (!this.icons[iconName]) {
      console.warn(`Icon "${iconName}" not found in DogGoods icon collection`);
      return false;
    }
    
    element.innerHTML = this.icons[iconName];
    
    // Apply custom classes
    if (className) {
      const svg = element.querySelector('svg');
      if (svg) {
        // Replace existing classes or add new ones
        svg.className = className;
      }
    }
    
    // Mark as loaded
    this.loadedIcons.add(element);
    
    // Trigger custom event
    element.dispatchEvent(new CustomEvent('iconLoaded', {
      detail: { iconName, element }
    }));
    
    return true;
  }
  
  // Initialize all icons on page
  initializeIcons() {
    const iconElements = document.querySelectorAll('[data-icon]');
    
    iconElements.forEach(element => {
      if (this.loadedIcons.has(element)) return;
      
      const iconName = element.dataset.icon;
      const iconClass = element.dataset.iconClass || '';
      
      if (iconName) {
        this.loadIcon(iconName, element, iconClass);
      }
    });
  }
  
  // Setup intersection observer for lazy loading
  setupLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers
      this.initializeIcons();
      return;
    }
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loadedIcons.has(entry.target)) {
          const iconName = entry.target.dataset.icon;
          const iconClass = entry.target.dataset.iconClass || '';
          
          if (iconName) {
            this.loadIcon(iconName, entry.target, iconClass);
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      rootMargin: '50px',
      threshold: 0.1
    });
    
    // Observe elements with data-icon-lazy attribute
    document.querySelectorAll('[data-icon-lazy]').forEach(element => {
      if (!this.observedElements.has(element)) {
        observer.observe(element);
        this.observedElements.add(element);
      }
    });
  }
  
  // Setup mutation observer for dynamically added elements
  setupMutationObserver() {
    if (!('MutationObserver' in window)) return;
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself has data-icon
            if (node.hasAttribute && node.hasAttribute('data-icon')) {
              this.processElement(node);
            }
            
            // Check for child elements with data-icon
            const iconElements = node.querySelectorAll ? node.querySelectorAll('[data-icon]') : [];
            iconElements.forEach(element => this.processElement(element));
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  processElement(element) {
    if (this.loadedIcons.has(element)) return;
    
    const iconName = element.dataset.icon;
    const iconClass = element.dataset.iconClass || '';
    const isLazy = element.hasAttribute('data-icon-lazy');
    
    if (iconName && !isLazy) {
      this.loadIcon(iconName, element, iconClass);
    }
  }
  
  // Utility method to create icon element
  createIcon(iconName, className = '') {
    if (!this.icons[iconName]) {
      console.warn(`Icon "${iconName}" not found in DogGoods icon collection`);
      return null;
    }
    
    const wrapper = document.createElement('span');
    wrapper.innerHTML = this.icons[iconName];
    
    const svg = wrapper.querySelector('svg');
    if (svg && className) {
      svg.className = className;
    }
    
    return svg;
  }
  
  // Get list of available icons
  getAvailableIcons() {
    return Object.keys(this.icons);
  }
  
  // Preload specific icons for better performance
  preloadIcons(iconNames) {
    iconNames.forEach(iconName => {
      if (this.icons[iconName]) {
        // Create a temporary element to "warm up" the icon
        const temp = document.createElement('div');
        temp.style.display = 'none';
        temp.innerHTML = this.icons[iconName];
        document.body.appendChild(temp);
        setTimeout(() => document.body.removeChild(temp), 0);
      }
    });
  }
  
  // Update icon in existing element
  updateIcon(element, newIconName, className = '') {
    if (this.loadedIcons.has(element)) {
      this.loadedIcons.delete(element);
    }
    
    element.dataset.icon = newIconName;
    if (className) {
      element.dataset.iconClass = className;
    }
    
    return this.loadIcon(newIconName, element, className);
  }
}

// Initialize global icon loader
window.DogGoodsIconLoader = new DogGoodsIconLoader();

// Convenience functions for global access
window.loadIcon = (iconName, element, className) => {
  return window.DogGoodsIconLoader.loadIcon(iconName, element, className);
};

window.createIcon = (iconName, className) => {
  return window.DogGoodsIconLoader.createIcon(iconName, className);
};

// jQuery integration if available
if (typeof jQuery !== 'undefined') {
  jQuery.fn.loadIcon = function(iconName, className) {
    return this.each(function() {
      window.DogGoodsIconLoader.loadIcon(iconName, this, className);
    });
  };
} 